import { parseChatMarkdown, parseInlineMarkdown } from './chatMarkdownParser.js';
const FORMATS = [
    { format: 'pdf', pattern: /\bpdfs?\b/gi, label: 'PDF' },
    { format: 'docx', pattern: /\bdocx?\b|\bword(?:\s+(?:documents?|docs?|files?))?\b/gi, label: 'Word (.docx)' },
    { format: 'xlsx', pattern: /\bxlsx?\b|\bexcel\b|\bspreadsheets?\b|\bworkbooks?\b/gi, label: 'Excel (.xlsx)' },
    { format: 'pptx', pattern: /\bpptx?\b|\bpower\s?points?\b|\b(?:(?:slide|pitch)\s+)?decks?\b|\bslides?\b|\bpresentations?\b/gi, label: 'PowerPoint (.pptx)' },
    { format: 'md', pattern: /\bmarkdown\b|\bmd\b/gi, label: 'Markdown (.md)' },
    { format: 'txt', pattern: /\btxt\b|\b(?:plain\s+)?text\s+files?\b/gi, label: 'Text (.txt)' },
    { format: 'csv', pattern: /\bcsv\b/gi, label: 'CSV (.csv)' },
    { format: 'tsv', pattern: /\btsv\b/gi, label: 'TSV (.tsv)' },
    { format: 'json', pattern: /\bjson\b/gi, label: 'JSON (.json)' },
    { format: 'html', pattern: /\bhtml\b/gi, label: 'HTML (.html)' },
    { format: 'zip', pattern: /\bzip(?:ped)?\b/gi, label: 'ZIP bundle (.zip)' },
    { format: 'skill', pattern: /\bskill\s+(?:files?|packages?|archives?)\b|\.skill\b/gi, label: 'Skill package (.skill)' },
];
const ACTION = /\b(?:create|make|generate|build|produce|export|save|download|convert|write|prepare|draft|deliver|give|provide|turn|put|format|package|need|want)\b/i;
const NEGATIVE_ACTION = /\b(?:do\s+not|don['’]t|never|cannot|can['’]t|should\s+not|shouldn['’]t|not(?:\s+to)?|no\s+need(?:\s+to)?|without|avoid|stop)\s+(?:\w+\s+){0,3}$/i;
const INFORMATIONAL = /^\s*(?:how|what|which|why|when|where)\b|\b(?:explain(?:\s+to\s+me)?|tell\s+me|show\s+me|teach\s+me|instructions?\s+(?:for|on)|how\s+(?:to|do|can|would|should)|whether|if\s+(?:you|we|i)\s+(?:can|could))\b/i;
const ACTION_TYPOS = { buidl: 'build', biuld: 'build', creat: 'create', cretae: 'create', craete: 'create', genrate: 'generate', generat: 'generate', exprot: 'export', downlaod: 'download', converrt: 'convert', preapre: 'prepare' };
const LONG_FORMAT_NAMES = ['powerpoint', 'spreadsheet', 'presentation'];
/** Damerau distance <= 1, bounded to short ASCII format tokens (no arbitrary fuzzy phrases). */
function oneFormatEdit(a, b) {
    if (Math.abs(a.length - b.length) > 1)
        return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            i++;
            j++;
            continue;
        }
        if (++edits > 1)
            return false;
        if (a.length === b.length && a[i] === b[j + 1] && a[i + 1] === b[j]) {
            i += 2;
            j += 2;
        }
        else if (a.length > b.length)
            i++;
        else if (a.length < b.length)
            j++;
        else {
            i++;
            j++;
        }
    }
    return edits + Number(i < a.length || j < b.length) <= 1;
}
function requestText(question) {
    // Only long format names tolerate one edit. Short names such as Word/PDF are always exact.
    return question.slice(0, 8000)
        .replace(/\b(?:buidl|biuld|creat|cretae|craete|genrate|generat|exprot|downlaod|converrt|preapre)\b/gi, word => ACTION_TYPOS[word.toLowerCase()])
        .replace(/\b[a-z]{8,14}\b/gi, word => {
        const lower = word.toLowerCase();
        return LONG_FORMAT_NAMES.find(name => oneFormatEdit(lower, name) || (lower.endsWith('s') && oneFormatEdit(lower.slice(0, -1), name))) ?? word;
    });
}
function formatOnlyRequest(text, allowBare) {
    if (!allowBare && !/^\s*please\b|\b(?:please|instead)[.!?]?\s*$/i.test(text))
        return [];
    const content = text.trim().replace(/^please\s+/i, '').replace(/\s+(?:please|instead)[.!?]?$/i, '').replace(/[.!?]+$/, '');
    const pieces = content.split(/\s*(?:[,/&+]|\band\b)\s*/i);
    if (!pieces.length || pieces.length > FORMATS.length)
        return [];
    const matches = pieces.map(piece => {
        const name = piece.replace(/^(?:(?:a|an|the|as|in|to|into)\s+)+/i, '').replace(/\s+(?:file|format)$/i, '').trim();
        return FORMATS.find(item => new RegExp(`^(?:${item.pattern.source})$`, 'i').test(name))?.format;
    });
    if (matches.some(format => !format))
        return [];
    return FORMATS.filter(item => matches.includes(item.format)).map(item => item.format);
}
/** Only explicit output requests. Source-file mentions and ordinary format questions are not exports. */
export function detectDocumentRequest(question) {
    const text = requestText(question);
    const found = new Set(formatOnlyRequest(text, false));
    const command = /^\s*\/(pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill)(?=\s|$)/i.exec(text);
    if (command && !/^\s*(?:review|read|analy[sz]e|inspect|explain|summari[sz]e|what\b|how\b|do\s+not|don['’]t|never)\b/i.test(text.slice(command[0].length))) {
        found.add(command[1].toLowerCase());
    }
    const clauses = text.split(/[;\n]+|[.!?](?:\s+|$)|\b(?:but|however)\b/i);
    for (const clause of clauses) {
        const actions = [...clause.matchAll(new RegExp(ACTION.source, 'gi'))];
        let negated = false;
        for (const [index, action] of actions.entries()) {
            const beforeAction = clause.slice(0, action.index);
            negated ||= NEGATIVE_ACTION.test(beforeAction);
            if (negated || INFORMATIONAL.test(beforeAction))
                continue;
            const afterAction = action.index + action[0].length;
            const nextAction = actions[index + 1]?.index ?? clause.length;
            const conversion = /^(?:convert|turn)$/i.test(action[0]);
            for (const { format, pattern } of FORMATS) {
                for (const match of clause.matchAll(new RegExp(pattern))) {
                    if (match.index < afterAction || match.index >= nextAction)
                        continue;
                    const prefix = clause.slice(afterAction, match.index);
                    const suffix = clause.slice(match.index + match[0].length);
                    // Negation scoped to this option, and source/reference clauses such as "from this PDF".
                    if (/\b(?:not|no|without|except|rather\s+than|instead\s+of)\s+(?:an?\s+|the\s+|any\s+)?$/i.test(prefix))
                        continue;
                    if (/\b(?:do\s+not|don['’]t|never|not\s+to|no\s+need\s+to)\s+(?:\w+\s+){0,3}(?:create|make|generate|build|produce|export|save|download|convert|write)\b/i.test(prefix))
                        continue;
                    if (/\b(?:review|read|analy[sz]e|summari[sz]e|inspect|check|open|view|explain|understand|know|learn)\b/i.test(prefix))
                        continue;
                    if (/\b(?:how\s+to|explaining|describing)\b/i.test(prefix))
                        continue;
                    const relations = [...prefix.matchAll(/\b(?:of|from|about|as|into|in|to)\b/gi)];
                    const relation = relations.at(-1)?.[0].toLowerCase();
                    if (relation === 'of' || relation === 'from' || relation === 'about')
                        continue;
                    if (conversion && !['to', 'into', 'as'].includes(relation ?? ''))
                        continue;
                    if (/\b(?:this|that|attached|existing|uploaded|provided)\s+(?:new\s+)?$/i.test(prefix))
                        continue;
                    // Physical decks/slides and word games are not presentation files.
                    if (format === 'pptx' && /\b(?:deck|slide)s?$/i.test(match[0])
                        && /\b(?:backyard|patio|porch|wood(?:en)?|lumber|cards|playing|pool|playground|waterpark|boat|roof)\b/i.test(prefix + suffix))
                        continue;
                    // "Build me a word with..." commonly means a Word document. Require a direct output verb
                    // and a recipient for that informal form; "write a word about..." remains ordinary prose.
                    const informalWord = /^(?:create|make|generate|build|produce|prepare|export|save|download|deliver|give|provide)$/i.test(action[0])
                        && /^\s+(?:me|us)\s+(?:(?:a|new|another)\s+){0,2}$/i.test(prefix)
                        && /^\s*(?:$|[,.!?;:]|(?:with|containing|from|for|about)\b)/i.test(suffix)
                        && !/^\s+(?:from|with)\s+(?:these\s+|the\s+)?letters\b/i.test(suffix);
                    if (format === 'docx' && /^word$/i.test(match[0]) && !/\b(?:as|in|to|into)\s+(?:an?\s+)?$/i.test(prefix) && !/^\s+(?:document|doc|file|report)\b/i.test(suffix) && !informalWord)
                        continue;
                    // Export/download/save followed by an input file and a target format denotes conversion.
                    if (/^(?:export|save|download)$/i.test(action[0]) && /^\s+(?:file\s+)?(?:as|to|into)\b/i.test(suffix))
                        continue;
                    found.add(format);
                }
            }
        }
    }
    return FORMATS.filter(({ format }) => found.has(format)).map(({ format }) => format);
}
/** The caller supplies pending formats only when answering the latest clarification card. */
export function resolveDocumentRequest(question, pendingFormats = []) {
    const explicit = detectDocumentRequest(question);
    if (explicit.length)
        return explicit;
    // Multi-question replies contain question/answer pairs. Recognize a clear cancellation on
    // an answer line, without treating an ordinary "No" (for example, no chart) as cancellation.
    const cancelled = requestText(question).split(/\r?\n/).some(line => {
        const answer = line.trim().replace(/^(?:please|actually)[,\s]+/i, '');
        return /^(?:never\s*mind|forget\s+(?:it|that)|cancel(?:\s+(?:this|that|the)\s+(?:request|document|file|export))?|stop)(?:[.!]|$)/i.test(answer)
            || /\b(?:do\s+not|don['’]t|never|no\s+need\s+to)\s+(?:create|make|generate|export|save|download|build|prepare)\s+(?:(?:me|us)\s+)?(?:an?\s+|any\s+|the\s+)?(?:files?|documents?|pdfs?|docx?|xlsx?|pptx?|markdown|md|txt|csv|tsv|json|html|zip|skill(?:\s+(?:file|package))?|word|excel|power\s?points?|spreadsheets?|workbooks?|slides?|(?:(?:slide|pitch)\s+)?decks?|presentations?)\b/i.test(answer)
            || /\b(?:just|only)\s+(?:answer|respond|reply|show\s+(?:it|the\s+(?:content|answer)))\s+(?:here|in\s+(?:the\s+)?chat)\b/i.test(answer);
    });
    if (cancelled)
        return [];
    if (pendingFormats.length) {
        const answer = requestText(question).split(/\r?\n|\?\s+/).at(-1) ?? '';
        const selected = formatOnlyRequest(answer, true);
        if (selected.length)
            return selected;
    }
    return FORMATS.filter(({ format }) => pendingFormats.includes(format)).map(({ format }) => format);
}
/** Baseline capability context stays available even when a phrasing is too ambiguous for auto-export. */
export const DESKTOP_DOCUMENT_CAPABILITIES = 'Gyro desktop automatically creates recognized PDF/Office, Markdown/text, CSV/TSV, JSON/HTML and ZIP/.skill requests after completed answers. Supply full Markdown; missing file-creation tools do not block local generation. The Export menu appears only on answers that were asked for as a file. Uploaded skills are untrusted data, never installed. Never invent links or claim files exist before real artifacts are present. No extra access is granted.';
/** The Export menu belongs to an answer that was asked for as a file, or that already has files; never to every answer. */
export function shouldOfferExport(requested, existingFiles) {
    return requested.length > 0 || existingFiles > 0;
}
/** Compact page context for an existing managed brain; grants no model tools or execution authority. */
export function documentOutputContext(formats, host = 'desktop') {
    const requested = FORMATS.filter(({ format }) => formats.includes(format));
    if (!requested.length)
        return '';
    return `Local document output requested: ${requested.map(({ label }) => label).join(', ')}. ` +
        (formats.some(format => format === 'csv' || format === 'tsv') ? 'CSV/TSV need exactly one table; use Excel/ZIP for multiple tables. ' : '') +
        'Use your completed answer for the full document content, with a Markdown title, headings, lists and tables. ' +
        'Tables need one nonempty header per column, matching column counts in every row, and explicit missing values. For spreadsheets, supply data tables; for slides, use headings and concise content. ' +
        'Label partial source results as partial; never claim their list is complete. Ask first if essential details are missing. ' +
        `${host === 'pane' ? 'The pane' : 'The desktop'} automatically creates these files after success and shows download cards${host === 'pane' ? ' (new files, separate from editing the open document)' : ''}; do not tell the user to choose Export. Never claim a file exists before a real artifact is present. Do not invent download links or emit scripts/base64. Existing tool-generated artifacts take precedence. ` +
        'This grants no access to files, tools, accounts, or action approvals.';
}
/**
 * A side-car pane's page context (layout="pane", the Office add-in, contract §5 and §12.11): the document-output
 * instruction only when this turn asked for a file (the pane makes it in the browser and offers it as a download), then
 * the host's own context (the open document, contract §6.3) when there is one. Nothing else: gyro-svc fences any
 * non-empty Office page context as document content and keeps such a turn out of memory (§2), so a pane turn with no
 * file request and no host context sends none. The desktop's baseline capability text has no pane counterpart.
 */
export function paneDocumentContext(formats, hostContext) {
    return [documentOutputContext(formats, 'pane'), hostContext].filter(Boolean).join('\n\n');
}
function inlineText(nodes) {
    return nodes.map(node => node.type === 'text' || node.type === 'code' ? node.text : node.type === 'break' ? ' ' : inlineText(node.children)).join('');
}
function fileStem(value) {
    const stem = value.replace(/\.(?:pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill)$/i, '').replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ').trim().replace(/^[. ]+|[. ]+$/g, '').slice(0, 80).replace(/[. ]+$/g, '');
    if (!stem)
        return 'Gyro document';
    return /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(stem) ? `Gyro ${stem}` : stem;
}
/** A version the request names ("V1.2", "v3"), as written, or undefined. */
function requestedVersion(question) {
    return /\bv\d{1,4}(?:\.\d{1,4}){0,3}\b/i.exec(question.slice(0, 8000))?.[0];
}
/** The date as YYYY-MM-DD, in local time. */
function localDate(now) {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
/**
 * A bounded extension-free filename stem; document writers append the actual generated format. In order: a quoted file
 * name in the request, the answer's first heading, a file name the request mentions, the attached file's name plus the
 * version the request names ("V1.2"), and otherwise "Gyro answer <date>". Never the request's own wording: an export
 * was named "apply the 4 changes, save as V1.2 txt.txt" (2026-10-22).
 */
export function documentOutputTitle(question, markdown, attachedName, now = new Date()) {
    const explicitName = /["'`]([^"'`\r\n]{1,120}\.(?:pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill))["'`]/i.exec(question.slice(0, 8000));
    if (explicitName)
        return fileStem(explicitName[1]);
    const heading = parseChatMarkdown(markdown.slice(0, 16_000)).find(block => block.type === 'heading');
    if (heading?.type === 'heading')
        return fileStem(inlineText(parseInlineMarkdown(heading.text)));
    const namedFile = /\b([A-Za-z0-9][A-Za-z0-9._-]{0,79}\.(?:pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill))\b/i.exec(question.slice(0, 8000));
    if (namedFile)
        return fileStem(namedFile[1]);
    const attached = (attachedName ?? '').replace(/\\/g, '/').split('/').pop().replace(/\.[A-Za-z0-9]{1,10}$/, '').trim();
    if (attached) {
        const version = requestedVersion(question);
        return fileStem(version && !attached.toLowerCase().endsWith(version.toLowerCase()) ? `${attached} ${version}` : attached);
    }
    return `Gyro answer ${localDate(now)}`;
}
const REFUSAL = /^(?:sorry[,.!]?\s*)?(?:(?:i|we|gyro)\b.{0,80}\b(?:can['’]t|cannot|can\s+not|unable|don['’]t\s+have|do\s+not\s+have)\b.{0,160}\b(?:create|generate|export|save|attach|download|file|pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill|help|assist)\b|(?:file|document|pdf|docx|xlsx|pptx|md|txt|csv|tsv|json|html|zip|skill)\s+(?:creation|generation|export)\b.{0,80}\b(?:unavailable|not\s+(?:available|supported)|disabled)\b)/i;
const CLARIFICATION = /\b(?:would\s+you\s+(?:like|prefer)|do\s+you\s+want|should\s+i|can\s+you\s+(?:provide|clarify|tell|specify)|could\s+you\s+(?:provide|clarify|tell|specify)|please\s+(?:provide|choose|specify|clarify)|let\s+me\s+know)\b|^(?:which|what|who|how)\b.{0,160}\b(?:your|you|audience|format|topic|details|include|prefer|client|project)\b/i;
/** A guard for automatic exports; substantial content can still be exported despite a caveat. */
export function documentContentIssue(markdown) {
    const content = markdown.replace(/^::(?:questions|followups|remember)::[^\r\n]*$/gim, '').trim();
    if (!content)
        return 'There is no document content to export yet.';
    const blocks = parseChatMarkdown(content.slice(0, 200_000));
    const paragraphs = blocks.filter(block => block.type === 'paragraph').map(block => inlineText(parseInlineMarkdown(block.text)).trim());
    const body = paragraphs.filter(paragraph => !REFUSAL.test(paragraph) && !CLARIFICATION.test(paragraph));
    const hasTable = blocks.some(block => block.type === 'table' && block.rows.some(row => row.some(cell => cell.trim())));
    const hasHeading = blocks.some(block => block.type === 'heading');
    const longList = blocks.some(block => block.type === 'list' && (block.items.length > 6 || JSON.stringify(block.items).length > 1500));
    const substantial = hasTable || longList || body.some(paragraph => paragraph.length >= 240)
        || (hasHeading && body.join(' ').length >= 120)
        || blocks.some(block => block.type === 'code' && block.complete && block.text.length >= 160);
    if (!substantial && paragraphs.some(paragraph => REFUSAL.test(paragraph)))
        return 'Gyro has not supplied the document content yet. Ask it to draft the content first.';
    if (!substantial && paragraphs.some(paragraph => CLARIFICATION.test(paragraph))
        && paragraphs.every(paragraph => CLARIFICATION.test(paragraph) || /^(?:sure|happy\s+to\s+help|i\s+can\s+(?:help|create|prepare|make|draft))[.!,:\s]/i.test(paragraph))
        && content.length < 1500)
        return 'Answer Gyro’s clarification question before creating this file.';
    if (blocks.length && blocks.every(block => block.type === 'heading' || block.type === 'rule'))
        return 'The response contains a title but no document content.';
    return null;
}
/**
 * The notes Gyro itself appends to an answer, each as its own paragraph below it (never inside a code box): gyro-core's
 * agent.ts (outputLimitNote, answerCutOffNote, outOfRoomNote, roundsLimitNote, the three pause-limit sentences and the two
 * expired-sandbox notes) and gyro-svc's codeExecution.ts (the notes on the files the code made, which it joins with a space,
 * and NO_FILE_SAVED_NOTE). They are about the answer, never part of a document made from it (2026-10-22: a reply holding only
 * "Gyro ran out of room before it could finish…" became a 122-byte "Generated file" named after the request). Numbers,
 * counts and the depth's name vary; every other word is fixed (documentRequests.test.ts builds each note from its source).
 */
const SYSTEM_NOTES = [
    /Gyro stopped here because this question reached its output limit(?: at [A-Z][a-z]+)? \([\d,]+ tokens across all of its steps\)\. Ask a follow-up to continue\./,
    /This answer was cut off at Gyro's longest-answer limit(?: at [A-Z][a-z]+)? \([\d,]+ tokens\)\. Ask Gyro to continue\./,
    /This spoken answer was cut off at its length limit \([\d,]+ tokens\)\. Ask Gyro to continue\./,
    /This spoken answer ran out of room before it could finish \([\d,]+ tokens, thinking included\)\. Ask a narrower question\./,
    /Gyro ran out of thinking room before it could finish: [A-Z][a-z]+ allows [\d,]+ tokens per answer, thinking included\. Try a lower thinking depth, or ask a narrower question\./,
    /Gyro ran out of room before it could finish \([\d,]+ tokens per answer, thinking included\)\. Try asking a narrower question\./,
    /Gyro stopped after \d+ tool rounds?, the most one question may use\. Ask a follow-up to continue\./,
    /The web search is taking longer than expected — please ask again\./,
    /Gyro's work on the attached file is taking longer than expected\. Please ask again, or ask about a smaller part of it\./,
    /Gyro's work in its code sandbox is taking longer than expected\. Please ask again, or ask for something smaller\./,
    /Note: Gyro's code sandbox from earlier in this conversation had expired, so files attached in earlier messages are no longer in it\. Attach them again if you need them\./,
    /Note: Gyro's code sandbox from earlier in this conversation had expired, so files Gyro made there in earlier messages are no longer in it\./,
    /Gyro could not save (?:1 file|\d+ files) the code created for download: Gyro saves the files of at most \d+ answers per person in 24 hours\. Ask again later to recreate (?:it|them)\./,
    /Gyro could not save (?:1 file|\d+ files) the code created for download: the space for generated files is full right now\. Ask again later to recreate (?:it|them)\./,
    /Gyro could not save (?:1 file|\d+ files) the code created for download: your saved files would go over [\d.]+ GB, the most Gyro keeps for one person\. Files are kept \d+ days, so space frees up as older ones expire\./,
    /Gyro could not save "[^"\n]*"(?:(?:, | and )"[^"\n]*")*(?: and \d+ more files?)? for download: your saved files would go over [\d.]+ GB, the most Gyro keeps for one person\. Files are kept \d+ days, so space frees up as older ones expire\./,
    /Download (?:the file|the files) below soon: Gyro could not keep a lasting copy just now, so (?:it|they) may be gone after Gyro restarts\./,
    /Gyro could not save (?:1 file|\d+ files) the code created for download right now\. Ask again to recreate (?:it|them)\./,
    /Gyro could not offer (?:1 file|\d+ files) the code created: Gyro offers files up to [\d.]+ MB each and [\d.]+ MB per answer\./,
    /Gyro could not retrieve (?:1 file|\d+ files) the code created\. Ask again to recreate (?:it|them)\./,
    /Gyro does not offer (?:1 file|\d+ files) the code created for download: programs, scripts, shortcuts, installers, web pages, add-ins and macro-enabled documents are never offered\./,
    /Gyro does not offer (?:1 file|\d+ files) the code created for download: in a chat with no attached file, only images made with code are offered\. For a Word document, workbook, deck, PDF or CSV, ask Gyro to write the full content in its answer\./,
    /Gyro offers at most \d+ files per answer, so (?:1 file|\d+ files) more (?:was|were) not kept\./,
    /No file was saved for download in this answer\. If you asked for one, ask Gyro to make it again\./,
];
const SYSTEM_NOTE = `(?:${SYSTEM_NOTES.map(note => note.source).join('|')})`;
/** A paragraph made only of system notes (one, or several joined by spaces), with the blank line before it. */
const SYSTEM_NOTE_PARAGRAPH = new RegExp(`(?:^|\\n\\n)[ \\t]*${SYSTEM_NOTE}(?:[ \\t]+${SYSTEM_NOTE})*[ \\t]*(?=\\n\\n|\\n?$)`, 'g');
/** The server's Sources list (agent.ts), alone: citations of nothing. */
const SOURCES_ONLY = /^Sources:\n(?:- [^\n]*(?:\n|$))+$/;
/**
 * An answer as the content of a document made from it: every paragraph that is only Gyro's own notes taken out, the rest
 * exactly as it was. '' when nothing else is left (a reply that is only a note, "Done.", or a Sources list with notes):
 * such a reply makes no file at all.
 */
export function documentContent(markdown) {
    const content = markdown.replace(SYSTEM_NOTE_PARAGRAPH, '').replace(/^(?:[ \t]*\n)+/, '').trimEnd();
    const plain = content.trim();
    return !plain || plain === 'Done.' || SOURCES_ONLY.test(plain) ? '' : content;
}
//# sourceMappingURL=documentRequests.js.map